import { useEffect, useRef, useState } from "react";
import { PNL_PAGE_ASSETS } from "../utils/pnlOgImage/pnlPageAssets";
import { loadImageAsset } from "../utils/pnlOgImage/loadImageAsset";
import { loadProfileImageForCanvas } from "../utils/pnlOgImage/loadProfileImageForCanvas";
import { prefetchProfileImageUrl } from "../utils/profileImageCache";

/**
 * Warm the browser cache for PNL tab visuals (background, ring art, stat icons, avatar).
 */
export function usePnlAssetPreload({ profileSrc, profileImageRef }) {
  const staticStartedRef = useRef(false);
  const [profileImageReady, setProfileImageReady] = useState(false);

  useEffect(() => {
    if (staticStartedRef.current) return undefined;
    staticStartedRef.current = true;

    const preloadLinks = PNL_PAGE_ASSETS.map((href) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = href;
      document.head.appendChild(link);
      return link;
    });

    Promise.allSettled(PNL_PAGE_ASSETS.map((src) => loadImageAsset(src)));

    return () => {
      preloadLinks.forEach((link) => link.remove());
    };
  }, []);

  useEffect(() => {
    if (!profileSrc) {
      setProfileImageReady(false);
      return undefined;
    }

    setProfileImageReady(false);
    let cancelled = false;

    prefetchProfileImageUrl(profileSrc);

    const preloadProfile = () => {
      loadProfileImageForCanvas(
        profileSrc,
        profileImageRef?.current ?? null
      ).catch(() => null);
    };

    preloadProfile();

    const domImg = profileImageRef?.current;
    const markReady = () => {
      if (!cancelled) setProfileImageReady(true);
    };

    if (domImg) {
      if (domImg.complete && domImg.naturalWidth > 0) {
        markReady();
      } else {
        domImg.addEventListener("load", markReady, { once: true });
        domImg.addEventListener("load", preloadProfile, { once: true });
      }
    }

    return () => {
      cancelled = true;
      if (domImg) {
        domImg.removeEventListener("load", markReady);
        domImg.removeEventListener("load", preloadProfile);
      }
    };
  }, [profileSrc, profileImageRef]);

  return { profileImageReady, setProfileImageReady };
}
